import { useState, useEffect } from "react";
import { Card, CardBody, CardHeader, Input, Alert, Row, Col } from "reactstrap";
import ApiCall from '../utils/apis';
import { isSuperAdmin } from '../utils/session';

const GREEN = '#2dce89';
const RED = '#f5365c';
const YELLOW = '#ffd600';

const styles = {
  card: (color) => ({
    background: '#111a33', borderRadius: 8, border: '1px solid #2a3558', borderTop: `3px solid ${color}`,
    padding: '16px 18px', color: '#c9d3ee', fontFamily: 'monospace', marginBottom: 16,
  }),
  title: { color: '#fff', fontWeight: 700, fontSize: 17 },
  muted: { color: '#8f9bbd', fontSize: 12 },
  big: { color: '#fff', fontWeight: 700, fontSize: 22 },
  pill: (color) => ({
    border: `1px solid ${color}`, color, background: 'rgba(255,255,255,0.04)',
    borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', whiteSpace: 'nowrap',
  }),
  button: (color) => ({
    background: 'rgba(255,255,255,0.04)', color, border: `1px solid ${color}`, borderRadius: 4,
    padding: '5px 12px', fontSize: 12, fontWeight: 700, fontFamily: 'monospace', cursor: 'pointer',
  }),
  row: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap' },
  input: { maxWidth: 320, fontFamily: 'monospace' },
};

const usd = (value) => `${Number(value || 0).toFixed(2)} USDT`;
const day = (iso) => (iso ? iso.slice(0, 10) : '—');

// Estado de la factura: texto y color
const state = (invoice) => {
  if (invoice.status === 'paid') return { text: 'PAGADA', color: GREEN };
  if (invoice.status === 'reported') return { text: 'EN REVISIÓN', color: YELLOW };
  if (invoice.overdue) return { text: 'VENCIDA', color: RED };
  return { text: 'PENDIENTE', color: '#8898aa' };
};

// Factura de un mes. `onReport` (dueño) y `onApprove`/`onReject` (super admin) deciden las acciones
const InvoiceCard = ({ invoice, busy, showOwner, onReport, onApprove, onReject }) => {
  const [text, setText] = useState("");
  const { text: label, color } = state(invoice);
  const disabled = busy !== null;
  const dim = (style) => ({ ...style, opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' });

  return (
    <div style={styles.card(color)}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <span style={styles.title}>{invoice.period}</span>
          {showOwner && <span style={{ ...styles.muted, marginLeft: 10 }}>👤 {invoice.userLogin}</span>}
          <div style={styles.muted}>
            Ganancia neta: <b style={{ color: '#c9d3ee' }}>{usd(invoice.profit)}</b> · Comisión {(invoice.rate * 100).toFixed(0)}%:{' '}
            <b style={{ color: '#fff' }}>{usd(invoice.amount)}</b> · Vence: {day(invoice.dueDate)}
          </div>
        </div>
        <span style={styles.pill(color)}>● {label}</span>
      </div>

      {invoice.paymentRef && <div style={{ ...styles.muted, marginTop: 8 }}>Referencia: <b style={{ color: '#c9d3ee' }}>{invoice.paymentRef}</b></div>}
      {invoice.note && <div style={{ color: RED, fontSize: 12, marginTop: 8 }}>Pago rechazado: {invoice.note}</div>}
      {invoice.status === 'paid' && <div style={{ ...styles.muted, marginTop: 8 }}>Pagada el {day(invoice.paidAt)}</div>}

      {onReport && invoice.status === 'pending' && (
        <div style={styles.row}>
          <Input bsSize="sm" style={styles.input} placeholder="Referencia o hash del pago" value={text} onChange={e => setText(e.target.value)} />
          <button style={dim(styles.button(GREEN))} disabled={disabled || !text.trim()} onClick={() => onReport(invoice, text.trim())}>
            {busy === invoice.id ? 'Enviando…' : 'Reportar pago'}
          </button>
        </div>
      )}

      {onApprove && invoice.status !== 'paid' && (
        <div style={styles.row}>
          <button style={dim(styles.button(GREEN))} disabled={disabled} onClick={() => onApprove(invoice)}>✔ Aprobar pago</button>
          {invoice.status === 'reported' && (
            <>
              <Input bsSize="sm" style={styles.input} placeholder="Motivo del rechazo (opcional)" value={text} onChange={e => setText(e.target.value)} />
              <button style={dim(styles.button(RED))} disabled={disabled} onClick={() => onReject(invoice, text.trim())}>✖ Rechazar</button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Comisión mensual: ganancia del mes, facturas del usuario y, para el super admin, las de todos
const Billing = ({ onChange }) => {
  const [data, setData] = useState(null);
  const [all, setAll] = useState([]);
  const [current, setCurrent] = useState([]);   // solo super admin: mes en curso de cada usuario
  const [busy, setBusy] = useState(null);
  const [message, setMessage] = useState(null);
  const superAdmin = isSuperAdmin();

  const load = async () => {
    const res = await ApiCall({ call: `billing`, method: "GET" });
    if (res && !res.error) setData(res);
    if (superAdmin) {
      const admin = await ApiCall({ call: `billing/admin`, method: "GET" });
      if (admin && admin.invoices) setAll(admin.invoices);
      if (admin && admin.current) setCurrent(admin.current);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (invoice, action, params, okText) => {
    setBusy(invoice.id);
    setMessage(null);
    const res = await ApiCall({ call: `billing/invoices/${invoice.id}/${action}`, params, method: 'POST' });
    setBusy(null);
    if (!res) {
      setMessage({ color: 'danger', text: 'No se pudo conectar con el servidor' });
    } else if (res.error) {
      setMessage({ color: 'danger', text: res.error });
    } else {
      setMessage({ color: 'success', text: okText });
    }
    await load();
    if (onChange) onChange();
  };

  const report = (invoice, reference) => run(invoice, 'report', { reference }, 'Pago reportado. Queda en revisión.');
  const approve = (invoice) => {
    if (!window.confirm(`Marcar como pagada la comisión ${invoice.period} de ${invoice.userLogin} (${usd(invoice.amount)})?`)) return;
    run(invoice, 'approve', {}, 'Pago aprobado');
  };
  const reject = (invoice, note) => run(invoice, 'reject', { note }, 'Pago rechazado');

  // Super admin: deja en cero la ganancia y la comisión del mes en curso de un usuario
  const resetMonth = async (row) => {
    if (!window.confirm(`¿Reiniciar a cero la comisión de ${row.period} de ${row.userLogin}?\n\n` +
      `La ganancia acumulada hasta ahora (${usd(row.profit)}) no se cobrará. Lo que gane desde este momento sí cuenta.`)) return;
    setBusy(`reset-${row.userId}`);
    setMessage(null);
    const res = await ApiCall({ call: `billing/users/${row.userId}/reset`, params: {}, method: 'POST' });
    setBusy(null);
    if (!res) setMessage({ color: 'danger', text: 'No se pudo conectar con el servidor' });
    else if (res.error) setMessage({ color: 'danger', text: res.error });
    else setMessage({ color: 'success', text: `Comisión de ${row.userLogin} reiniciada (se dejaron de contar ${usd(res.profitBefore)})` });
    await load();
    if (onChange) onChange();
  };

  if (!data) return <p className="text-center text-muted" style={{ marginTop: 40 }}>Cargando…</p>;

  const percent = (data.rate * 100).toFixed(0);

  return (
    <>
    <Card className="shadow" style={{ marginTop: 20 }}>
      <CardHeader className="border-0">
        <h3 className="mb-0">💳 Comisiones</h3>
      </CardHeader>
      <CardBody>
        {message && <Alert color={message.color} toggle={() => setMessage(null)}>{message.text}</Alert>}
        {data.blocked && (
          <Alert color="danger">Tienes una comisión vencida. No puedes crear, levantar ni eliminar bots hasta reportar el pago.</Alert>
        )}

        <Row>
          <Col lg="6">
            <div style={styles.card('#5e72e4')}>
              <div style={styles.muted}>Mes en curso · {data.current.period}</div>
              <div style={{ display: 'flex', gap: 32, marginTop: 8, flexWrap: 'wrap' }}>
                <div>
                  <div style={styles.muted}>Ganancia neta</div>
                  <div style={{ ...styles.big, color: data.current.profit < 0 ? RED : '#fff' }}>{usd(data.current.profit)}</div>
                </div>
                <div>
                  <div style={styles.muted}>Comisión estimada ({percent}%)</div>
                  <div style={styles.big}>{usd(data.current.fee)}</div>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        <p className="text-muted" style={{ fontSize: 13 }}>
          {data.exempt
            ? 'Tu cuenta está exenta de comisión.'
            : <>Al cerrar cada mes se cobra el {percent}% de la ganancia neta de todos tus bots (ganancias menos pérdidas).
              Si el mes cierra sin ganancia no se cobra nada. Tienes hasta el día {data.graceDays} del mes siguiente para
              reportar el pago; después no podrás crear, levantar ni eliminar bots.</>}
        </p>

        {data.invoices.length === 0
          ? !data.exempt && <p className="text-center text-muted">Aún no tienes comisiones.</p>
          : data.invoices.map(invoice => <InvoiceCard key={invoice.id} invoice={invoice} busy={busy} onReport={report} />)
        }
      </CardBody>
    </Card>

    {superAdmin && (
      <Card className="shadow" style={{ marginTop: 20, marginBottom: 20 }}>
        <CardHeader className="border-0">
          <h3 className="mb-0">👥 Comisiones de los usuarios</h3>
        </CardHeader>
        <CardBody>
          <div style={styles.card('#5e72e4')}>
            <div style={styles.title}>Mes en curso por usuario</div>
            <div style={{ ...styles.muted, marginBottom: 8 }}>
              Reiniciar deja en cero lo acumulado del mes; no borra el historial y lo que gane después sí se cobra.
            </div>
            {current.length === 0 && <div style={styles.muted}>No hay usuarios.</div>}
            {current.map(row => (
              <div key={row.userId} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '8px 0',
                borderTop: '1px solid #2a3558', flexWrap: 'wrap' }}>
                <span style={{ ...styles.title, fontSize: 14, minWidth: 120 }}>{row.userLogin}</span>
                <span style={{ minWidth: 150 }}>Ganancia <b style={{ color: row.profit < 0 ? RED : '#fff' }}>{usd(row.profit)}</b></span>
                <span style={{ minWidth: 150 }}>Comisión <b style={{ color: '#fff' }}>{usd(row.fee)}</b></span>
                <span style={{ ...styles.muted, minWidth: 150 }}>Último reinicio: {day(row.lastReset)}</span>
                <button style={{ ...styles.button(YELLOW), marginLeft: 'auto' }} disabled={busy === `reset-${row.userId}`}
                  onClick={() => resetMonth(row)}>
                  {busy === `reset-${row.userId}` ? '⏳' : '↺'} Reiniciar a cero
                </button>
              </div>
            ))}
          </div>
          {all.length === 0 && <p className="text-center text-muted">Ningún usuario tiene comisiones.</p>}
          {all.map(invoice => (
            <InvoiceCard key={invoice.id} invoice={invoice} busy={busy} showOwner onApprove={approve} onReject={reject} />
          ))}
        </CardBody>
      </Card>
    )}
    </>
  );
};

export default Billing;
